import classNames from 'classnames';

import Button from '@/components/shared/Button/Button';
import { ContentStatus, JourneySummaryHub } from '@addmile/backend-client';

import LanguageFlag from '../../../Content/components/LanguageFlag/LanguageFlag';
import { count } from '../../count';
import JourneyStatusSelect from '../JourneyStatusSelect/JourneyStatusSelect';

interface JourneyCardProps {
  journey: JourneySummaryHub;
  onEdit: (id: string) => void;
  onCopy: (id: string) => void;
  onDelete: (journey: JourneySummaryHub) => void;
  onStatusChange: (journey: JourneySummaryHub, status: ContentStatus) => void;
}

const JourneyCard = ({ journey, onEdit, onCopy, onDelete, onStatusChange }: JourneyCardProps) => {
  const isOnline = journey.status === ContentStatus.Online;
  const counts = [
    count(journey.milesCount, 'mile'),
    count(journey.stepsCount, 'step'),
    count(journey.blocksCount, 'block'),
  ];

  return (
    <div
      className="flex items-center justify-between gap-4 rounded-2xl bg-white-100 p-4 cursor-pointer"
      onClick={() => onEdit(journey.id)}
    >
      <div className="flex gap-4 items-center min-w-0">
        {journey.imageUrl ? (
          <img
            src={journey.imageUrl}
            alt={journey.title}
            className="w-16 h-16 rounded-xl object-cover bg-neutral-10"
          />
        ) : (
          <div className="w-16 h-16 rounded-xl bg-neutral-10" />
        )}
        <div className="flex flex-col gap-2 min-w-0">
          <p className="text-xl font-bold text-neutral-90 truncate">
            {journey.title || 'Untitled journey'}
          </p>
          <div className="flex gap-2 flex-wrap">
            {counts.map((count) => (
              <span
                key={count}
                className="text-sm px-2 py-1 rounded-lg bg-neutral-10 text-neutral-70"
              >
                {count}
              </span>
            ))}
            <span
              className={classNames('text-sm px-2 py-1 rounded-lg', {
                'bg-success-10 text-success-60': journey.issuesCount === 0,
                'bg-danger-10 text-danger-60': journey.issuesCount > 0,
              })}
            >
              {journey.issuesCount === 0
                ? 'Ready to publish'
                : `${journey.issuesCount} to fix before publishing`}
            </span>
          </div>
        </div>
      </div>

      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        className="flex items-center gap-3 shrink-0"
      >
        <LanguageFlag language={journey.language} />
        <JourneyStatusSelect
          status={journey.status}
          onChange={(status) => onStatusChange(journey, status)}
        />
        <Button
          isIcon
          className="!border-neutral-20 ml-2"
          variant="transparent"
          prefixIcon={<img src="/icons/pencil.svg" alt="edit" />}
          onClick={() => onEdit(journey.id)}
        />
        <Button
          isIcon
          className="!border-neutral-20"
          variant="transparent"
          prefixIcon={<img src="/icons/copy.svg" alt="duplicate" />}
          onClick={() => onCopy(journey.id)}
        />
        <span title={isOnline ? 'Take the journey offline before deleting it' : undefined}>
          <Button
            isIcon
            className={classNames('border-none', { 'opacity-30': isOnline })}
            variant="transparent"
            disabled={isOnline}
            prefixIcon={<img src="/icons/trash.svg" alt="delete" />}
            onClick={() => onDelete(journey)}
          />
        </span>
      </div>
    </div>
  );
};

export default JourneyCard;
